//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useState } from 'react';

import { ListItemIcon, Menu, MenuItem } from '@mui/material';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';

import HeaderControl from './HeaderControl';

interface HeaderIdentityMenuProps {
  // Null until a profile has been read, which is reachable on first paint.
  username: string | null;
  onOpenSettings: () => void;
  // Handed the control this menu hangs off, so the tools popover opens under
  // the same button the user just used. Absent when there are no tools, which
  // omits the row rather than offering an empty one.
  onOpenTools?: (anchorEl: HTMLElement) => void;
  onLogout: () => void;
  // False in single-user mode, where signing out strands the only account.
  canSignOut?: boolean;
}

/**
 * Who is signed in, and everything that belongs to them: their settings and
 * the way out.
 *
 * The name is the label rather than a bare hamburger, and that is the whole
 * defense against a misread. Users trained on the old menu will look here for
 * Plugins and Settings-for-everything; a control that says *your name* reads
 * as "you" rather than as "everything else", which is what stops them.
 *
 * Nothing deployment-wide belongs in here. Administration changes what every
 * user sees, and a personal menu is exactly the wrong place to say so -- that
 * lives in its own control.
 *
 * Tools passes that test on the strength of the pinning: the list is the same
 * for everyone, but which of it a user keeps on their taskbar is stored per
 * user and is the reason to open the row at all.
 */
const HeaderIdentityMenu = ({
  username, onOpenSettings, onOpenTools, onLogout, canSignOut = true
}: HeaderIdentityMenuProps) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const close = () => setAnchor(null);

  const pick = (action: () => void) => () => {
    close();
    action();
  };

  return (
    <>
      <HeaderControl
        icon={<MenuIcon fontSize="inherit" />}
        label={username ?? 'Account'}
        description={username ?
          `${username} — your settings and sign out` : undefined}
        popup="menu"
        isExpanded={anchor !== null}
        onClick={(event) => setAnchor(event.currentTarget)}
      />
      <Menu open={anchor !== null} anchorEl={anchor} onClose={close}>
        {onOpenTools && anchor && (
          // Opens a popover rather than a submenu: its rows each carry a pin
          // switch, and a `menuitem` may not contain a focusable descendant.
          <MenuItem
            aria-haspopup="dialog"
            onClick={pick(() => onOpenTools(anchor))}
          >
            <ListItemIcon>
              <HomeRepairServiceIcon fontSize="small" />
            </ListItemIcon>
            Tools
          </MenuItem>
        )}
        <MenuItem onClick={pick(onOpenSettings)}>
          <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
          Settings
        </MenuItem>
        {canSignOut && (
          <MenuItem onClick={pick(onLogout)}>
            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
            Sign out
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default HeaderIdentityMenu;
